import { useState, useEffect, useRef } from "react"
import { ArrowLeft, Check, X, Save, AlertTriangle, Trash2, CircleSlash, Camera, FileText } from "lucide-react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Progress } from "@/components/ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { PageHeader } from "@/components/PageHeader"
import PhotoUpload from "@/components/PhotoUpload"
import PhotoUploadPrincipal from "@/components/PhotoUploadPrincipal"
import { cn } from "@/lib/utils"
import { formatarMoedaInput, parsearMoeda } from "@/lib/utils"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { useAuth } from "@/contexts/AuthContext"
import {
  PHOTO_BUCKET,
  createSignedFileUrl,
  type UploadablePhoto,
  uploadFileToBucket,
} from "@/lib/vehicleStorage"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AssistenteVozContextual } from "@/components/ai/AssistenteVozContextual"
import { preencherAvaliacao, type CriterioCompacto, type ClienteInfo } from "@/lib/aiFillFormService"
interface Avaliacao {
  id: string
  veiculo_id: string
  status: string
  nota_final?: number
  updated_at?: string
  observacoes_iniciais?: string
  cliente_nome?: string | null
  cliente_telefone?: string | null
  veiculos: {
    placa: string
    marca: string
    modelo: string
    ano: number
    cor: string
    quilometragem: number
    foto_principal?: string | null
    foto_principal_path?: string | null
  }
}

interface Criterio {
  id: string
  categoria: string
  nome: string
  descricao: string
}

interface AvaliacaoCriterio {
  criterio_id: string
  observacoes: string
  aprovado: boolean | null
  status?: VisualStatus
  custoReparo?: number
  fotos?: UploadablePhoto[]
}

type CriterioFieldValue = AvaliacaoCriterio[keyof AvaliacaoCriterio]
type VisualStatus = "pendente" | "aprovado" | "atencao" | "reprovado" | "na"

interface FotoPrincipal {
  [categoria: string]: UploadablePhoto | string
}

interface GalleryZoomState {
  url: string
  title: string
  subtitle?: string
}

const grupos = [
  { nome: "Externo", subcategorias: ["Frontal", "Traseira", "Lateral Direita", "Lateral Esquerda"] },
  { nome: "Mecânica", subcategorias: ["Motor", "Painel e Instrumentos"] },
  { nome: "Interior", subcategorias: ["Interior"] },
  { nome: "Detalhes extras", subcategorias: ["Detalhes Extras"] }
]

const subcategoriasOrdenadas = grupos.flatMap((grupo) => grupo.subcategorias)

const CRITERIOS_LOJA_COMPLETO: Criterio[] = [
  { id: "frontal_1", categoria: "Frontal", nome: "Parachoque", descricao: "Estado do parachoque dianteiro (riscos, amassados, rachaduras)" },
  { id: "frontal_2", categoria: "Frontal", nome: "Grade frontal", descricao: "Estado da grade frontal (integridade, limpeza, danos)" },
  { id: "frontal_3", categoria: "Frontal", nome: "Faróis", descricao: "Funcionamento e estado dos faróis dianteiros" },
  { id: "frontal_4", categoria: "Frontal", nome: "Capô", descricao: "Estado do capô (alinhamento, amassados, pintura)" },
  { id: "frontal_5", categoria: "Frontal", nome: "Para-brisa", descricao: "Estado do para-brisa (trincas, arranhões, vedação)" },
  { id: "frontal_6", categoria: "Frontal", nome: "Emblema/marca", descricao: "Estado do emblema/marca dianteira" },
  { id: "frontal_7", categoria: "Frontal", nome: "Placa dianteira", descricao: "Estado e fixação da placa dianteira" },
  { id: "traseira_1", categoria: "Traseira", nome: "Parachoque traseiro", descricao: "Estado do parachoque traseiro" },
  { id: "traseira_2", categoria: "Traseira", nome: "Lanternas", descricao: "Funcionamento e estado das lanternas traseiras" },
  { id: "traseira_3", categoria: "Traseira", nome: "Tampa do porta-malas", descricao: "Estado da tampa do porta-malas" },
  { id: "traseira_4", categoria: "Traseira", nome: "Emblema/marca", descricao: "Estado do emblema/marca traseira" },
  { id: "traseira_5", categoria: "Traseira", nome: "Placa traseira", descricao: "Estado e fixação da placa traseira" },
  { id: "traseira_6", categoria: "Traseira", nome: "Saída de escapamento", descricao: "Estado da saída de escapamento" },
  { id: "latdir_1", categoria: "Lateral Direita", nome: "Portas e maçanetas", descricao: "Estado das portas e maçanetas do lado direito" },
  { id: "latdir_2", categoria: "Lateral Direita", nome: "Retrovisor", descricao: "Estado e funcionamento do retrovisor direito" },
  { id: "latdir_3", categoria: "Lateral Direita", nome: "Vidros laterais", descricao: "Estado dos vidros laterais do lado direito" },
  { id: "latdir_5", categoria: "Lateral Direita", nome: "Para-lamas", descricao: "Estado dos para-lamas do lado direito" },
  { id: "latdir_6", categoria: "Lateral Direita", nome: "Frisos e adesivos", descricao: "Estado dos frisos e adesivos do lado direito" },
  { id: "latesq_1", categoria: "Lateral Esquerda", nome: "Portas e maçanetas", descricao: "Estado das portas e maçanetas do lado esquerdo" },
  { id: "latesq_2", categoria: "Lateral Esquerda", nome: "Retrovisor", descricao: "Estado e funcionamento do retrovisor esquerdo" },
  { id: "latesq_3", categoria: "Lateral Esquerda", nome: "Vidros laterais", descricao: "Estado dos vidros laterais do lado esquerdo" },
  { id: "latesq_5", categoria: "Lateral Esquerda", nome: "Para-lamas", descricao: "Estado dos para-lamas do lado esquerdo" },
  { id: "latesq_6", categoria: "Lateral Esquerda", nome: "Frisos e adesivos", descricao: "Estado dos frisos e adesivos do lado esquerdo" },
  { id: "motor_1", categoria: "Motor", nome: "Compartimento limpo", descricao: "Estado de limpeza do compartimento do motor" },
  { id: "motor_2", categoria: "Motor", nome: "Bateria", descricao: "Estado e fixação da bateria" },
  { id: "motor_3", categoria: "Motor", nome: "Correias e mangueiras", descricao: "Estado das correias e mangueiras" },
  { id: "motor_4", categoria: "Motor", nome: "Filtros", descricao: "Estado dos filtros do motor" },
  { id: "motor_5", categoria: "Motor", nome: "Sinais de vazamento ou ferrugem", descricao: "Presença de vazamentos ou ferrugem no motor" },
  { id: "painel_1", categoria: "Painel e Instrumentos", nome: "Velocímetro e conta-giros", descricao: "Funcionamento do velocímetro e conta-giros" },
  { id: "painel_2", categoria: "Painel e Instrumentos", nome: "Luzes de advertência", descricao: "Funcionamento das luzes de advertência do painel" },
  { id: "painel_3", categoria: "Painel e Instrumentos", nome: "Sistema multimídia/rádio", descricao: "Funcionamento do sistema multimídia e rádio" },
  { id: "painel_4", categoria: "Painel e Instrumentos", nome: "Ar-condicionado e controles", descricao: "Funcionamento do ar-condicionado e controles do painel" },
  { id: "interior_1", categoria: "Interior", nome: "Bancos (dianteiros e traseiros)", descricao: "Estado dos bancos dianteiros e traseiros" },
  { id: "interior_2", categoria: "Interior", nome: "Forros de portas", descricao: "Estado dos forros de portas" },
  { id: "interior_3", categoria: "Interior", nome: "Tapetes", descricao: "Estado dos tapetes" },
  { id: "interior_4", categoria: "Interior", nome: "Volante", descricao: "Estado do volante" },
  { id: "interior_5", categoria: "Interior", nome: "Espaço interno geral", descricao: "Conservação e limpeza do espaço interno" },
  { id: "extras_1", categoria: "Detalhes Extras", nome: "Porta-malas", descricao: "Estado do porta-malas" },
  { id: "extras_2", categoria: "Detalhes Extras", nome: "Rodas", descricao: "Estado das rodas (dianteiras e traseiras)" },
  { id: "extras_3", categoria: "Detalhes Extras", nome: "Pneus", descricao: "Estado dos pneus (dianteiros e traseiros)" },
  { id: "extras_4", categoria: "Detalhes Extras", nome: "Espelhos e retrovisores", descricao: "Estado dos espelhos e retrovisores" },
]

const CRITERIOS_OFICINA_LEVE: Criterio[] = [
  { id: "oficina_frontal_1", categoria: "Frontal", nome: "Frente do veículo", descricao: "Danos visíveis, avarias, faróis, para-choque e para-brisa" },
  { id: "oficina_traseira_1", categoria: "Traseira", nome: "Traseira do veículo", descricao: "Danos visíveis, lanternas, para-choque e tampa traseira" },
  { id: "oficina_latdir_1", categoria: "Lateral Direita", nome: "Lateral direita", descricao: "Riscos, amassados, vidros, retrovisor e portas do lado direito" },
  { id: "oficina_latesq_1", categoria: "Lateral Esquerda", nome: "Lateral esquerda", descricao: "Riscos, amassados, vidros, retrovisor e portas do lado esquerdo" },
  { id: "oficina_painel_1", categoria: "Painel e Instrumentos", nome: "Quilometragem e painel", descricao: "Quilometragem, luzes acesas e alertas relevantes no painel" },
  { id: "oficina_interior_1", categoria: "Interior", nome: "Interior e pertences", descricao: "Estado geral interno, bancos, acabamento e itens deixados no veículo" },
]

const getFriendlySectionLabel = (subcat: string) => {
  const map: Record<string, string> = {
    Frontal: "Frente",
    Traseira: "Traseira",
    "Lateral Direita": "Lateral direita",
    "Lateral Esquerda": "Lateral esquerda",
    Motor: "Motor",
    "Painel e Instrumentos": "Painel",
    Interior: "Interior",
    "Detalhes Extras": "Detalhes extras",
  }

  return map[subcat] ?? subcat
}

const getStageInstruction = (subcat: string) => {
  const map: Record<string, string> = {
    Frontal: "Envie a foto da frente para liberar os critérios desta etapa.",
    Traseira: "Envie a foto de trás para liberar os critérios desta etapa.",
    "Lateral Direita": "Envie a foto da lateral direita para liberar os critérios desta etapa.",
    "Lateral Esquerda": "Envie a foto da lateral esquerda para liberar os critérios desta etapa.",
    Motor: "Envie a foto do motor para liberar os critérios desta etapa.",
    "Painel e Instrumentos": "Envie a foto do painel para liberar os critérios desta etapa.",
    Interior: "Envie a foto do interior para liberar os critérios desta etapa.",
    "Detalhes Extras": "Envie a foto desta etapa para liberar os critérios relacionados.",
  }

  return map[subcat] ?? "Envie a foto desta etapa para liberar os critérios relacionados."
}

const getStatusFromLegacyApproval = (aprovado: boolean | null): VisualStatus => {
  if (aprovado === true) return "aprovado"
  if (aprovado === false) return "reprovado"
  return "pendente"
}

const getLegacyApprovalFromStatus = (status: VisualStatus): boolean | null => {
  if (status === "aprovado") return true
  if (status === "reprovado") return false
  return null
}

const isSchemaCompatError = (error: unknown) => {
  const message = typeof error === "object" && error && "message" in error ? String(error.message) : ""
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : ""

  return (
    code === "42P10" ||
    message.includes("status") ||
    message.includes("on conflict") ||
    message.includes("ON CONFLICT") ||
    message.includes("avaliacao_id,criterio_id")
  )
}

const LEGACY_CRITERIA_SAVE_MODE_KEY = "weauto:avaliacao360:legacy-criteria-save-mode"

const persistLegacyCriteriaSaveMode = (enabled: boolean) => {
  if (typeof window === "undefined") return

  if (enabled) {
    window.sessionStorage.setItem(LEGACY_CRITERIA_SAVE_MODE_KEY, "true")
    return
  }

  window.sessionStorage.removeItem(LEGACY_CRITERIA_SAVE_MODE_KEY)
}

const Avaliacao360 = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const vertical = useTipoOperacao()
  const { loja } = useAuth()
  const fotosObrigatorias = loja?.fotos_entrada_obrigatorias ?? true
  const mostrarCustos = vertical.avaliacao.mostrarCustos
  const textosAvaliacao = vertical.avaliacao
  const fluxoLeveOficina = textosAvaliacao.criteriosTemplate === "oficina_leve"
  const criteriosBase = textosAvaliacao.criteriosTemplate === "oficina_leve"
    ? CRITERIOS_OFICINA_LEVE
    : CRITERIOS_LOJA_COMPLETO
  const subcategoriasDaAvaliacao = subcategoriasOrdenadas.filter((subcat) =>
    criteriosBase.some((criterio) => criterio.categoria === subcat)
  )
  const gruposDaAvaliacao = grupos
    .map((grupo) => ({
      ...grupo,
      subcategorias: grupo.subcategorias.filter((subcat) => subcategoriasDaAvaliacao.includes(subcat)),
    }))
    .filter((grupo) => grupo.subcategorias.length > 0)
  
  const [avaliacao, setAvaliacao] = useState<Avaliacao | null>(null)
  const [criterios, setCriterios] = useState<Criterio[]>([])
  const [avaliacoesCriterios, setAvaliacoesCriterios] = useState<Record<string, AvaliacaoCriterio>>({})
  const [custoDigitado, setCustoDigitado] = useState<Record<string, string>>({})
  const [isEditing, setIsEditing] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [originalAvaliacoesCriterios, setOriginalAvaliacoesCriterios] = useState<Record<string, AvaliacaoCriterio>>({})
  const [grupoAtual, setGrupoAtual] = useState<string>(gruposDaAvaliacao[0]?.nome ?? grupos[0].nome)
  const [fotosPrincipais, setFotosPrincipais] = useState<FotoPrincipal>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [iaProcessando, setIaProcessando] = useState(false)
  const [visualStatusPorCriterio, setVisualStatusPorCriterio] = useState<Record<string, VisualStatus>>({})
  const [autosaveTick, setAutosaveTick] = useState(0)
  const [photosDialogOpen, setPhotosDialogOpen] = useState(false)
  const [photoTab, setPhotoTab] = useState<"reparos" | "atencao" | "gerais">("reparos")
  const [photoZoom, setPhotoZoom] = useState<GalleryZoomState | null>(null)
  const autosaveTimerRef = useRef<number | null>(null)
  const saveInFlightRef = useRef(false)
  const queuedAutosaveRef = useRef(false)
  const legacyCriteriaSaveModeRef = useRef(
    typeof window !== "undefined" &&
      window.sessionStorage.getItem(LEGACY_CRITERIA_SAVE_MODE_KEY) === "true"
  )
  const initializedGroupRef = useRef(false)

  useEffect(() => {
    const digitados: Record<string, string> = {}
    Object.entries(avaliacoesCriterios).forEach(([id, criterio]) => {
      if (
        getVisualStatus(id) === 'reprovado' &&
        typeof criterio.custoReparo === 'number' &&
        criterio.custoReparo > 0
      ) {
        digitados[id] = formatarMoedaInput(criterio.custoReparo)
      } else {
        digitados[id] = ''
      }
    })
    setCustoDigitado(digitados)
  }, [avaliacoesCriterios])
  
  const handleCustoChange = (criterioId: string, valor: string) => {
    setCustoDigitado(prev => ({ ...prev, [criterioId]: valor }))
  }

  const handleCustoBlur = (criterioId: string) => {
    const valorDigitado = (custoDigitado[criterioId] || '').trim();
    const valorNumerico = parsearMoeda(valorDigitado);
    const valorFormatado = valorDigitado ? formatarMoedaInput(valorNumerico) : '';
    setCustoDigitado(prev => ({ ...prev, [criterioId]: valorFormatado }));
    updateCriterio(criterioId, 'custoReparo', valorNumerico);
  }

  // Atualiza um campo de um critério de avaliação
  const updateCriterio = (criterioId: string, campo: keyof AvaliacaoCriterio, valor: CriterioFieldValue) => {
    setAvaliacoesCriterios(prev => ({
      ...prev,
      [criterioId]: {
        ...prev[criterioId],
        [campo]: valor
      }
    }))
    setHasChanges(true)
    setAutosaveTick((prev) => prev + 1)
  }

  // Atualiza a foto principal de uma categoria
  const updateFotoPrincipal = (categoria: string, foto: UploadablePhoto | string | null) => {
    setFotosPrincipais(prev => ({
      ...prev,
      [categoria]: foto || ""
    }))
    setHasChanges(true)
    setAutosaveTick((prev) => prev + 1)
  }

  const reutilizarFotoCapaComoFotoFrontal = async () => {
    if (!id || !avaliacao?.veiculos?.foto_principal) return

    const fotoCapaPath = avaliacao.veiculos.foto_principal_path || avaliacao.veiculos.foto_principal
    const fotoReutilizada: UploadablePhoto = {
      url: avaliacao.veiculos.foto_principal,
      storagePath: fotoCapaPath,
    }

    try {
      const { error: deleteError } = await supabase
        .from('fotos_veiculo')
        .delete()
        .eq('avaliacao_id', id)
        .eq('categoria', 'Frontal')
        .ilike('descricao', '%principal%')

      if (deleteError) {
        console.error("Erro ao preparar reutilização da foto de capa:", deleteError)
        toast.error("Erro ao reutilizar foto de capa")
        return
      }

      const { error: insertError } = await supabase
        .from('fotos_veiculo')
        .insert({
          avaliacao_id: id,
          categoria: 'Frontal',
          url: fotoCapaPath,
          descricao: 'Foto principal - Frontal',
        })

      if (insertError) {
        console.error("Erro ao vincular foto de capa à avaliação:", insertError)
        toast.error("Erro ao reutilizar foto de capa")
        return
      }

      setFotosPrincipais(prev => ({
        ...prev,
        Frontal: fotoReutilizada,
      }))
      toast.success("Foto de capa usada como foto da entrada")
    } catch (error) {
      console.error("Erro inesperado ao reutilizar foto de capa:", error)
      toast.error("Erro inesperado ao reutilizar foto de capa")
    }
  }

  const updateObservacoesIniciais = (valor: string) => {
    setAvaliacao(prev => (
      prev
        ? {
            ...prev,
            observacoes_iniciais: valor,
          }
        : prev
    ))
    setHasChanges(true)
    setAutosaveTick((prev) => prev + 1)
  }

  const updateClienteEntrada = (campo: "cliente_nome" | "cliente_telefone", valor: string) => {
    setAvaliacao(prev => (
      prev
        ? {
            ...prev,
            [campo]: valor,
          }
        : prev
    ))
    setHasChanges(true)
    setAutosaveTick((prev) => prev + 1)
  }

  const getVisualStatus = (criterioId: string): VisualStatus => {
    return visualStatusPorCriterio[criterioId] || "pendente"
  }

  const setVisualStatus = (criterioId: string, status: VisualStatus) => {
    setVisualStatusPorCriterio((prev) => ({
      ...prev,
      [criterioId]: status,
    }))
    setHasChanges(true)
    setAutosaveTick((prev) => prev + 1)
  }

  const handleVisualStatusChange = (criterioId: string, status: VisualStatus) => {
    setVisualStatus(criterioId, status)
    updateCriterio(criterioId, "status", status)
    updateCriterio(criterioId, "aprovado", getLegacyApprovalFromStatus(status))
  }

  const handleVozAvaliacao = async (transcricao: string, contexto: string | null): Promise<string | null> => {
    const criteriosCompactos: CriterioCompacto[] = criterios.map(c => ({
      id: c.id,
      nome: c.nome,
    }))

    const mapStatus = (s: "ok" | "atencao" | "reprovado" | "na"): VisualStatus =>
      s === "ok" ? "aprovado" : s

    setIaProcessando(true)
    try {
      const { criterios: findings, cliente, mensagem } = await preencherAvaliacao(transcricao, criteriosCompactos, contexto)

      findings.forEach(({ criterio_id, status, observacoes, custo_reparo }) => {
        handleVisualStatusChange(criterio_id, mapStatus(status))
        if (observacoes) {
          updateCriterio(criterio_id, "observacoes", observacoes)
        }
        if (custo_reparo !== null) {
          updateCriterio(criterio_id, "custoReparo", custo_reparo)
        }
      })

      if (cliente.nome) updateClienteEntrada("cliente_nome", cliente.nome)
      if (cliente.telefone) updateClienteEntrada("cliente_telefone", cliente.telefone)

      return mensagem || null
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao processar voz"
      toast.error(message)
      return null
    } finally {
      setIaProcessando(false)
    }
  }

  const contarAprovados = () => criterios.filter((c) => getVisualStatus(c.id) === "aprovado").length
  const contarReprovados = () => criterios.filter((c) => getVisualStatus(c.id) === "reprovado").length
  const contarPendentes = () => criterios.filter((c) => getVisualStatus(c.id) === "pendente").length
  const contarConcluidos = () => criterios.filter((c) => getVisualStatus(c.id) !== "pendente").length
  const calcularCustoTotal = () => {
    if (!mostrarCustos) return 0

    let custoTotal = 0
    criterios.forEach(criterio => {
      const avaliacao = avaliacoesCriterios[criterio.id]
      if (avaliacao && getVisualStatus(criterio.id) === 'reprovado' && typeof avaliacao.custoReparo === 'number') {
        custoTotal += avaliacao.custoReparo
      }
    })
    return custoTotal
  }
  const calcularProgressoGeral = () => {
    const total = criterios.length
    const avaliados = contarConcluidos()
    return total > 0 ? Math.round((avaliados / total) * 100) : 0
  }

  const getCriteriosDaSubcategoria = (subcat: string) =>
    criterios.filter((criterio) => criterio.categoria === subcat)

  const getPendenciasDaSubcategoria = (subcat: string) =>
    getCriteriosDaSubcategoria(subcat).filter((criterio) => getVisualStatus(criterio.id) === "pendente").length

  const getStatusDaSubcategoria = (subcat: string) => {
    const temFotoPrincipal = Boolean(fotosPrincipais[subcat])
    if (!temFotoPrincipal) return "aguardando_foto" as const

    const pendencias = getPendenciasDaSubcategoria(subcat)
    if (pendencias > 0) return "avaliando" as const

    return "concluida" as const
  }

  const getGrupoDaSubcategoria = (subcat: string) =>
    gruposDaAvaliacao.find((grupo) => grupo.subcategorias.includes(subcat))?.nome ?? gruposDaAvaliacao[0]?.nome ?? grupos[0].nome

  const irParaProximaEtapa = (subcat: string) => {
    setGrupoAtual(getGrupoDaSubcategoria(subcat))
  }

  const salvarProgressoEmSegundoPlano = () => {
    void handleSalvarProgresso({ silent: true }).then((salvoComSucesso) => {
      if (!salvoComSucesso) {
        toast.error("Alterações pendentes. Tente salvar novamente antes de finalizar.")
      }
    })
  }

  // --- Carregamento e inicialização ---
  // Funções de carregamento e manipulação de dados
  const fetchAvaliacao = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('avaliacoes')
        .select(`
          *,
          veiculos:veiculo_id (
            placa,
            marca,
            modelo,
            ano,
            cor,
            quilometragem,
            foto_principal
          )
        `)
        .eq('id', id)
        .single()
      if (error) {
        toast.error(`Erro ao carregar ${vertical.labels.labelPrincipal.toLowerCase()}`)
        return
      }
      const fotoPrincipalUrl = await createSignedFileUrl(PHOTO_BUCKET, data.veiculos?.foto_principal)
      setAvaliacao({
        ...data,
        veiculos: {
          ...data.veiculos,
          foto_principal: fotoPrincipalUrl ?? data.veiculos?.foto_principal,
          foto_principal_path: data.veiculos?.foto_principal,
        },
      })
      if (data) {
        await fetchAvaliacoesCriterios(data.id)
      }
    } catch (error) {
      toast.error("Erro inesperado")
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAvaliacoesCriterios = async (avaliacaoId: string) => {
    try {
      const { data, error } = await supabase
        .from('avaliacoes_criterios')
        .select('*')
        .eq('avaliacao_id', avaliacaoId)
      if (error) {
        initializeAvaliacoesCriterios()
        return
      }

      legacyCriteriaSaveModeRef.current = false
      persistLegacyCriteriaSaveMode(false)

      if (data && data.length > 0) {
        const criteriosSalvos: Record<string, AvaliacaoCriterio> = {}
        const statusVisuais: Record<string, VisualStatus> = {}
        data.forEach(item => {
          const status = (item.status as VisualStatus | null) ?? getStatusFromLegacyApproval(item.aprovado)
          criteriosSalvos[item.criterio_id] = {
            criterio_id: item.criterio_id,
            observacoes: item.observacoes || "",
            aprovado: item.aprovado,
            status,
            custoReparo: item.custo_reparo || 0
          }
          statusVisuais[item.criterio_id] = status
        })
        setAvaliacoesCriterios(criteriosSalvos)
        setVisualStatusPorCriterio(statusVisuais)
        // Após carregar os critérios, carregar as fotos dos critérios reprovados
        await fetchFotosCriterios()
      } else {
        initializeAvaliacoesCriterios()
      }
    } catch (error) {
      initializeAvaliacoesCriterios()
    }
  }

  const fetchFotosPrincipais = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('fotos_veiculo')
        .select('categoria, url')
        .eq('avaliacao_id', id)
        .ilike('descricao', '%principal%')

      if (error) {
        console.error("Erro ao carregar fotos principais:", error)
        return
      }

      if (data && data.length > 0) {
        const fotosCarregadas: FotoPrincipal = {}
        await Promise.all(data.map(async (foto) => {
          fotosCarregadas[foto.categoria] = {
            url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
            storagePath: foto.url,
          }
        }))
        setFotosPrincipais(fotosCarregadas)
      }
    } catch (error) {
      console.error("Erro inesperado ao carregar fotos principais:", error)
    }
  }

  const fetchFotosCriterios = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('fotos_veiculo')
        .select('categoria, url')
        .eq('avaliacao_id', id)
        .or('categoria.like.criterio_reprovado_%,categoria.like.criterio_atencao_%')

      if (error) {
        console.error("Erro ao carregar fotos de critérios:", error)
        return
      }

      if (data && data.length > 0) {
        const fotosResolvidas = await Promise.all(data.map(async (foto) => ({
          categoria: foto.categoria,
          url: (await createSignedFileUrl(PHOTO_BUCKET, foto.url)) ?? foto.url,
          storagePath: foto.url,
        })))

        setAvaliacoesCriterios(prev => {
          const updated = { ...prev }
          fotosResolvidas.forEach(foto => {
            const criterioId = foto.categoria
              .replace('criterio_reprovado_', '')
              .replace('criterio_atencao_', '')
            if (updated[criterioId]) {
              updated[criterioId] = {
                ...updated[criterioId],
                fotos: updated[criterioId].fotos ? [...updated[criterioId].fotos, foto] : [foto]
              }
            }
          })
          return updated
        })
      }
    } catch (error) {
      console.error("Erro inesperado ao carregar fotos de critérios:", error)
    }
  }

  const handleSalvarProgresso = async (options?: { silent?: boolean }) => {
    if (saveInFlightRef.current) {
      if (options?.silent) {
        queuedAutosaveRef.current = true
        return true
      }

      toast.info("Já existe um salvamento em andamento")
      return false
    }

    if (!id) {
      if (!options?.silent) {
        toast.error(`ID da ${vertical.labels.labelPrincipal.toLowerCase()} não encontrado`)
      }
      return false
    }

    if (!hasChanges) {
      if (!options?.silent) {
        toast.info("Não há alterações para salvar")
      }
      return true
    }

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
    }

    saveInFlightRef.current = true
    setSaving(true)
    try {
      const criteriosAtivos = new Set(criterios.map((criterio) => criterio.id))
      const criteriosEntries = Object.entries(avaliacoesCriterios).filter(([criterioId]) =>
        criteriosAtivos.has(criterioId)
      )
      const unsupportedStatuses = criteriosEntries.some(([criterioId]) => {
        const status = getVisualStatus(criterioId)
        return status === "atencao" || status === "na"
      })

      const dadosParaSalvar = criteriosEntries.map(([criterioId, criterio]) => ({
        avaliacao_id: id,
        criterio_id: criterioId,
        aprovado: getLegacyApprovalFromStatus(getVisualStatus(criterioId)),
        observacoes: criterio.observacoes || '',
        custo_reparo: mostrarCustos && getVisualStatus(criterioId) === "reprovado" ? (criterio.custoReparo || 0) : 0,
        nota: 5.0,
        status: getVisualStatus(criterioId),
      }))

      const salvarModoLegado = async () => {
        if (unsupportedStatuses) {
          if (!options?.silent) {
            toast.error("Atenção e N/A ainda exigem a migration do banco para salvar.")
          }
          return false
        }

        const dadosLegados = criteriosEntries.map(([criterioId, criterio]) => ({
          avaliacao_id: id,
          criterio_id: criterioId,
          aprovado: getLegacyApprovalFromStatus(getVisualStatus(criterioId)),
          observacoes: criterio.observacoes || '',
          custo_reparo: mostrarCustos && getVisualStatus(criterioId) === "reprovado" ? (criterio.custoReparo || 0) : 0,
          nota: 5.0,
        }))

        const { error: deleteCriteriosError } = await supabase
          .from('avaliacoes_criterios')
          .delete()
          .eq('avaliacao_id', id)

        if (deleteCriteriosError) {
          console.error("Erro ao limpar critérios existentes:", deleteCriteriosError)
          if (!options?.silent) {
            toast.error("Erro ao salvar progresso")
          }
          return false
        }

        const { error: legacyInsertError } = await supabase
          .from('avaliacoes_criterios')
          .insert(dadosLegados)

        if (legacyInsertError) {
          console.error("Erro ao salvar progresso no modo legado:", legacyInsertError)
          if (!options?.silent) {
            toast.error("Erro ao salvar progresso")
          }
          return false
        }

        return true
      }

      const deveTentarModoNovo = !legacyCriteriaSaveModeRef.current || unsupportedStatuses

      if (deveTentarModoNovo) {
        const { error } = await supabase
          .from('avaliacoes_criterios')
          .upsert(dadosParaSalvar, { onConflict: 'avaliacao_id,criterio_id' })

        if (!error) {
          legacyCriteriaSaveModeRef.current = false
          persistLegacyCriteriaSaveMode(false)
        } else {
          if (!isSchemaCompatError(error)) {
            console.error("Erro ao salvar progresso:", error)
            if (!options?.silent) {
              toast.error("Erro ao salvar progresso")
            }
            return false
          }

          legacyCriteriaSaveModeRef.current = true
          persistLegacyCriteriaSaveMode(true)
          const legacySaved = await salvarModoLegado()
          if (!legacySaved) {
            return false
          }
        }
      } else {
        const legacySaved = await salvarModoLegado()
        if (!legacySaved) {
          return false
        }
      }

      // Salvar fotos principais
      const fotosPrincipaisParaSalvar = (await Promise.all(
        Object.entries(fotosPrincipais)
          .filter(([_, foto]) => Boolean(foto))
          .map(async ([categoria, foto]) => {
            if (!foto) return null

            if (typeof foto !== "string" && foto.file) {
              const { path } = await uploadFileToBucket(
                PHOTO_BUCKET,
                foto.file,
                `avaliacoes/${id}/principais/${categoria.toLowerCase().replace(/\s+/g, "-")}`,
              )

              return {
                avaliacao_id: id,
                categoria,
                url: path,
                descricao: `Foto principal - ${categoria}`,
              }
            }

            return {
              avaliacao_id: id,
              categoria,
              url: typeof foto === "string" ? foto : (foto.storagePath ?? foto.url),
              descricao: `Foto principal - ${categoria}`,
            }
          }),
      )).filter(Boolean)

      if (fotosPrincipaisParaSalvar.length > 0) {
        // Primeiro, deletar fotos principais existentes para esta avaliação
        const { error: deleteError } = await supabase
          .from('fotos_veiculo')
          .delete()
          .eq('avaliacao_id', id)
          .ilike('descricao', '%principal%')

        if (deleteError) {
          console.error("Erro ao deletar fotos principais existentes:", deleteError)
        }

        // Inserir as novas fotos principais
        const { error: fotosError } = await supabase
          .from('fotos_veiculo')
          .insert(fotosPrincipaisParaSalvar)

        if (fotosError) {
          console.error("Erro ao salvar fotos principais:", fotosError)
          if (!options?.silent) {
            toast.error("Erro ao salvar fotos principais")
          }
          return false
        }
      }

      // Salvar fotos de critérios reprovados
      const fotosCriteriosParaSalvar: Array<{
        avaliacao_id: string
        categoria: string
        url: string
        descricao: string
      }> = []
      for (const [criterioId, criterio] of Object.entries(avaliacoesCriterios)) {
        const visualStatus = getVisualStatus(criterioId)
        if ((visualStatus === "reprovado" || visualStatus === "atencao") && criterio.fotos && criterio.fotos.length > 0) {
          const categoriaPrefix = visualStatus === "reprovado" ? "criterio_reprovado_" : "criterio_atencao_"
          for (const foto of criterio.fotos) {
            let fotoPath = foto.storagePath ?? foto.url
            if (foto.file) {
              const { path } = await uploadFileToBucket(
                PHOTO_BUCKET,
                foto.file,
                `avaliacoes/${id}/criterios/${criterioId}`,
              )
              fotoPath = path
            }

            fotosCriteriosParaSalvar.push({
              avaliacao_id: id,
              categoria: `${categoriaPrefix}${criterioId}`,
              url: fotoPath,
              descricao: `Foto do critério ${visualStatus} - ${criterioId}`
            })
          }
        }
      }

      if (fotosCriteriosParaSalvar.length > 0) {
        // Primeiro, deletar fotos de critérios existentes para esta avaliação
        const { error: deleteCriteriosError } = await supabase
          .from('fotos_veiculo')
          .delete()
          .eq('avaliacao_id', id)
          .or('categoria.like.criterio_reprovado_%,categoria.like.criterio_atencao_%')

        if (deleteCriteriosError) {
          console.error("Erro ao deletar fotos de critérios existentes:", deleteCriteriosError)
        }

        // Inserir as novas fotos de critérios
        const { error: fotosCriteriosError } = await supabase
          .from('fotos_veiculo')
          .insert(fotosCriteriosParaSalvar)

        if (fotosCriteriosError) {
          console.error("Erro ao salvar fotos de critérios:", fotosCriteriosError)
          if (!options?.silent) {
            toast.error("Erro ao salvar fotos de critérios")
          }
          return false
        }
      }

      // Verificar se a avaliação está 100% completa e atualizar status
      const progressoAtual = calcularProgressoGeral()
      const dadosAtualizacao: {
        updated_at: string
        observacoes_iniciais: string
        cliente_nome?: string | null
        cliente_telefone?: string | null
        status?: string
      } = {
        updated_at: new Date().toISOString(),
        observacoes_iniciais: avaliacao?.observacoes_iniciais || "",
      }

      if (fluxoLeveOficina) {
        dadosAtualizacao.cliente_nome = avaliacao?.cliente_nome?.trim() || null
        dadosAtualizacao.cliente_telefone = avaliacao?.cliente_telefone?.trim() || null
      }
      
      if (progressoAtual === 100 && evidenciasMinimasCompletas && avaliacao?.status !== 'concluida') {
        dadosAtualizacao.status = 'concluida'
      } else if ((progressoAtual < 100 || !evidenciasMinimasCompletas) && avaliacao?.status === 'pendente') {
        dadosAtualizacao.status = 'ativa'
      }

      // Atualizar o timestamp e status da avaliação
      const { error: updateError } = await supabase
        .from('avaliacoes')
        .update(dadosAtualizacao)
        .eq('id', id)

      if (updateError) {
        console.error("Erro ao atualizar avaliação:", updateError)
      } else if (dadosAtualizacao.status) {
        // Atualizar o estado local se o status foi alterado
        setAvaliacao(prev => prev ? { ...prev, status: dadosAtualizacao.status } : null)
      }

      setHasChanges(false)
      setOriginalAvaliacoesCriterios({ ...avaliacoesCriterios })
      if (!options?.silent) {
        toast.success("Progresso salvo com sucesso!")
      }
      return true
    } catch (error) {
      console.error("Erro inesperado ao salvar progresso:", error)
      if (!options?.silent) {
        toast.error("Erro inesperado ao salvar progresso")
      }
      return false
    } finally {
      saveInFlightRef.current = false
      setSaving(false)

      if (queuedAutosaveRef.current) {
        queuedAutosaveRef.current = false
        window.setTimeout(() => {
          void handleSalvarProgresso({ silent: true })
        }, 0)
      }
    }
  }

  const handleSalvarEAvancar = async (subcatAtual: string) => {
    const indiceAtual = subcategoriasDaAvaliacao.findIndex((subcat) => subcat === subcatAtual)
    const proximaSubcategoria = subcategoriasDaAvaliacao[indiceAtual + 1]

    salvarProgressoEmSegundoPlano()

    if (!proximaSubcategoria) {
      toast.success(`Etapa concluída. ${vertical.labels.labelPrincipal} pronta para revisão final.`)
      return
    }

    irParaProximaEtapa(proximaSubcategoria)
    toast.success(`Etapa concluída. Próxima etapa: ${getFriendlySectionLabel(proximaSubcategoria)}.`)
  }

  const handleFinalizarAvaliacao = async () => {
    if (!avaliacaoCompleta) return

    if (!evidenciasMinimasCompletas) {
      const pendentes = fotosPrincipaisPendentes.map(getFriendlySectionLabel).join(", ")
      toast.info(`Adicione as fotos obrigatórias antes de finalizar: ${pendentes}.`)
      return
    }

    if (saveInFlightRef.current) {
      toast.info(`Aguarde o salvamento terminar para finalizar a ${vertical.labels.labelPrincipal.toLowerCase()}.`)
      return
    }

    if (hasChanges) {
      const salvoComSucesso = await handleSalvarProgresso({ silent: true })
      if (!salvoComSucesso) return
    }

    if (avaliacao?.status !== "concluida" && id) {
      const { error } = await supabase
        .from("avaliacoes")
        .update({ status: "concluida", updated_at: new Date().toISOString() })
        .eq("id", id)

      if (error) {
        console.error(`Erro ao finalizar ${vertical.labels.labelPrincipal.toLowerCase()}:`, error)
        toast.error(`Erro ao finalizar ${vertical.labels.labelPrincipal.toLowerCase()}`)
        return
      }

      setAvaliacao((prev) => (prev ? { ...prev, status: "concluida" } : prev))
    }

    toast.success(`${vertical.labels.labelPrincipal} concluída com sucesso.`)
    navigate(`/proposta/${id}`)
  }

  const handleDeleteAvaliacao = async () => {
    if (!id) {
      toast.error(`ID da ${vertical.labels.labelPrincipal.toLowerCase()} não encontrado`)
      return
    }

    try {
      console.log(`Iniciando exclusão da ${vertical.labels.labelPrincipal.toLowerCase()}:`, id)
      
      // Primeiro, deletar todos os critérios de avaliação relacionados
      const { error: criteriosError } = await supabase
        .from('avaliacoes_criterios')
        .delete()
        .eq('avaliacao_id', id)

      if (criteriosError) {
        console.error("Erro ao deletar critérios:", criteriosError)
        toast.error(`Erro ao deletar critérios da ${vertical.labels.labelPrincipal.toLowerCase()}`)
        return
      }

      console.log("Critérios deletados com sucesso")

      // Depois, deletar todas as fotos relacionadas
      const { error: fotosError } = await supabase
        .from('fotos_veiculo')
        .delete()
        .eq('avaliacao_id', id)

      if (fotosError) {
        console.error("Erro ao deletar fotos:", fotosError)
        toast.error(`Erro ao deletar fotos da ${vertical.labels.labelPrincipal.toLowerCase()}`)
        return
      }

      console.log("Fotos deletadas com sucesso")

      // Por último, deletar a avaliação
      const { error: avaliacaoError } = await supabase
        .from('avaliacoes')
        .delete()
        .eq('id', id)

      if (avaliacaoError) {
        console.error(`Erro ao deletar ${vertical.labels.labelPrincipal.toLowerCase()}:`, avaliacaoError)
        toast.error(`Erro ao deletar ${vertical.labels.labelPrincipal.toLowerCase()}`)
        return
      }

      console.log(`${vertical.labels.labelPrincipal} deletada com sucesso`)
      toast.success(`${vertical.labels.labelPrincipal} deletada com sucesso`)
      navigate('/avaliacoes')
    } catch (error) {
      console.error(`Erro inesperado ao deletar ${vertical.labels.labelPrincipal.toLowerCase()}:`, error)
      toast.error(`Erro inesperado ao deletar ${vertical.labels.labelPrincipal.toLowerCase()}`)
    }
  }

  useEffect(() => {
    initializedGroupRef.current = false
    fetchAvaliacao()
    fetchFotosPrincipais()
    fetchFotosCriterios()
    setCriterios(criteriosBase)
  // eslint-disable-next-line
  }, [id])

  useEffect(() => {
    if (loading || !id || !hasChanges) return

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current)
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      void handleSalvarProgresso({ silent: true })
    }, 1200)

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current)
        autosaveTimerRef.current = null
      }
    }
  }, [autosaveTick, hasChanges, loading, id])

  useEffect(() => {
    if (loading || initializedGroupRef.current || criterios.length === 0) return

    const primeiraIncompleta =
      subcategoriasDaAvaliacao.find((subcat) => getStatusDaSubcategoria(subcat) !== "concluida") ||
      subcategoriasDaAvaliacao[0] ||
      grupos[0].subcategorias[0]

    setGrupoAtual(getGrupoDaSubcategoria(primeiraIncompleta))
    initializedGroupRef.current = true
  }, [loading, criterios.length, avaliacoesCriterios, fotosPrincipais])

  const initializeAvaliacoesCriterios = () => {
      const inicial: Record<string, AvaliacaoCriterio> = {}
      const isCompleted = avaliacao?.status === 'concluida'
      const defaultStatus: VisualStatus = isCompleted ? "aprovado" : "pendente"
      const defaultApprovalStatus = getLegacyApprovalFromStatus(defaultStatus)
      criteriosBase.forEach(criterio => {
        inicial[criterio.id] = {
          criterio_id: criterio.id,
          observacoes: isCompleted ? "Critério avaliado" : "",
          aprovado: defaultApprovalStatus,
          status: defaultStatus,
        }
      })
    setAvaliacoesCriterios(inicial)
    setVisualStatusPorCriterio(
      Object.fromEntries(
        criteriosBase.map((criterio) => [
          criterio.id,
          defaultStatus,
        ]),
      ),
    )
  }

  // ...fetchAvaliacao, fetchAvaliacoesCriterios, fetchFotosPrincipais, fetchFotosCriterios, handleSalvarProgresso, handleDeleteAvaliacao (sem alteração) ...

  // --- JSX PRINCIPAL ---
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!avaliacao) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">{vertical.labels.labelPrincipal} não encontrada</p>
        <Button asChild className="mt-4">
          <Link to="/avaliacoes">Voltar para Avaliações</Link>
        </Button>
      </div>
    )
  }

  const progressoGeral = calcularProgressoGeral()
  const totalConcluidos = contarConcluidos()
  const totalSubcategorias = subcategoriasDaAvaliacao.length
  const fotosPrincipaisPendentes = subcategoriasDaAvaliacao.filter((subcat) => !fotosPrincipais[subcat])
  const evidenciasMinimasCompletas = !fluxoLeveOficina || !fotosObrigatorias || fotosPrincipaisPendentes.length === 0
  const etapaAtual = subcategoriasDaAvaliacao.find((subcat) => getStatusDaSubcategoria(subcat) !== "concluida") || subcategoriasDaAvaliacao[subcategoriasDaAvaliacao.length - 1]
  const indiceEtapaAtual =
    subcategoriasDaAvaliacao.findIndex((subcat) => subcat === etapaAtual) + 1
  const proximaEtapa = subcategoriasDaAvaliacao[subcategoriasDaAvaliacao.findIndex((subcat) => subcat === etapaAtual) + 1]
  const etapaAtualCompleta = getStatusDaSubcategoria(etapaAtual) === "concluida"
  const avaliacaoCompleta = progressoGeral === 100
  const avaliacaoProntaParaFinalizar = avaliacaoCompleta && evidenciasMinimasCompletas
  const avaliacaoFinalizada = avaliacao?.status === "concluida"
  const grupoDaEtapaAtual = getGrupoDaSubcategoria(etapaAtual)
  const usuarioEstaNoGrupoDaEtapaAtual = grupoAtual === grupoDaEtapaAtual
  const ctaPrincipalAvanca =
    (!avaliacaoFinalizada &&
      (avaliacaoCompleta || (!fluxoLeveOficina && (!usuarioEstaNoGrupoDaEtapaAtual || etapaAtualCompleta))))
    || (fluxoLeveOficina && avaliacaoFinalizada && hasChanges)
  const deveMostrarObservacoesGerais = fluxoLeveOficina || !proximaEtapa || avaliacaoCompleta || avaliacaoFinalizada

  const handleIrParaEtapaAtual = () => {
    setGrupoAtual(grupoDaEtapaAtual)
    toast.info(`Continue em ${getFriendlySectionLabel(etapaAtual)}.`)
  }

  const ctaPrincipalLabel = (avaliacaoFinalizada && !fluxoLeveOficina)
    ? textosAvaliacao.ctaFinalizado
    : (avaliacaoFinalizada && fluxoLeveOficina)
      ? (hasChanges ? "Salvar alterações" : textosAvaliacao.ctaFinalizado)
    : avaliacaoCompleta && !evidenciasMinimasCompletas
      ? "Adicionar fotos obrigatórias"
    : avaliacaoProntaParaFinalizar
      ? textosAvaliacao.ctaFinalizar
    : !usuarioEstaNoGrupoDaEtapaAtual
      ? `Continuar para ${getFriendlySectionLabel(etapaAtual)}`
      : etapaAtualCompleta
        ? proximaEtapa
          ? `Continuar para ${getFriendlySectionLabel(proximaEtapa)}`
          : "Concluir etapa"
        : "Salvar progresso"
  const desabilitarCtaPrincipal =
    (avaliacaoFinalizada && !fluxoLeveOficina) ||
    (avaliacaoFinalizada && fluxoLeveOficina && !hasChanges) ||
    (avaliacaoProntaParaFinalizar && saving) ||
    (fluxoLeveOficina
      ? (!avaliacaoCompleta && !hasChanges)
      : (usuarioEstaNoGrupoDaEtapaAtual && !etapaAtualCompleta && !hasChanges))

  const summaryCards = [
    {
      label: "Progresso",
      value: `${totalConcluidos}/${criterios.length}`,
      note: `${progressoGeral}% concluído`,
      progress: progressoGeral,
    },
    {
      label: textosAvaliacao.labelCriticos,
      value: contarReprovados(),
      note: mostrarCustos ? "Itens com ajuste" : "Danos registrados",
    },
    mostrarCustos ? {
      label: "Custo",
      value: `R$ ${calcularCustoTotal().toFixed(2)}`,
      note: "Impacto acumulado",
    } : {
      label: "Atenção",
      value: criterios.filter((c) => getVisualStatus(c.id) === "atencao").length,
      note: "Pontos observados",
    },
  ]

  const reparosIdentificados = Object.entries(avaliacoesCriterios)
    .filter(([, criterio]) => criterio.status === "reprovado" || criterio.aprovado === false)
    .map(([criterioId, criterio]) => {
      const criterioBase = criterios.find((item) => item.id === criterioId)

      return {
        criterioId,
        nome: criterioBase?.nome || criterioId,
        categoria: criterioBase?.categoria || "",
        custo: criterio.custoReparo || 0,
        observacoes: criterio.observacoes,
        foto: criterio.fotos?.[0],
      }
    })

  const atencaoIdentificados = Object.entries(avaliacoesCriterios)
    .filter(([, criterio]) => criterio.status === "atencao")
    .map(([criterioId, criterio]) => {
      const criterioBase = criterios.find((item) => item.id === criterioId)

      return {
        criterioId,
        nome: criterioBase?.nome || criterioId,
        categoria: criterioBase?.categoria || "",
        observacoes: criterio.observacoes,
        foto: criterio.fotos?.[0],
      }
    })

  const fotosGerais = subcategoriasDaAvaliacao
    .map((subcat) => {
      const foto = fotosPrincipais[subcat]
      if (!foto) return null

      const url = typeof foto === "string" ? foto : foto.url
      return {
        url,
        title: getFriendlySectionLabel(subcat),
        subtitle: avaliacao.veiculos.placa,
      }
    })
    .filter((item): item is { url: string; title: string; subtitle: string } => Boolean(item?.url))

  return (
    <div className="space-y-6">
      <PageHeader
        title={textosAvaliacao.tituloPagina}
        description={`${avaliacao.veiculos.placa} · ${avaliacao.veiculos.marca} ${avaliacao.veiculos.modelo} · ${avaliacao.veiculos.ano}`}
      >
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => { setPhotoTab("reparos"); setPhotosDialogOpen(true) }}>
            <Camera className="mr-2 h-4 w-4" />
            {mostrarCustos ? "Fotos e reparos" : "Fotos da entrada"}
          </Button>
          <Button variant="outline" asChild>
            <Link to="/avaliacoes">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para {vertical.labels.menuPrincipal.toLowerCase()}
            </Link>
          </Button>
        </div>
      </PageHeader>

      <div className="sticky top-0 z-20 space-y-3 bg-background/95 pb-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <Card className="border-border/70 shadow-sm">
          <CardContent className="space-y-2 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <h2 className="truncate text-sm font-semibold tracking-tight text-foreground md:text-base">
                    {getFriendlySectionLabel(etapaAtual)}
                  </h2>
                  <span className="text-[11px] text-muted-foreground md:text-xs">
                    Etapa {indiceEtapaAtual}/{totalSubcategorias}
                  </span>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-data text-xs font-semibold text-foreground md:text-sm">{totalConcluidos}/{criterios.length}</p>
                <p className="text-[10px] text-muted-foreground md:text-[11px]">
                  {saving ? "Salvando..." : hasChanges ? "Alterações pendentes" : "Salvo"}
                </p>
              </div>
            </div>
            <Progress value={progressoGeral} className="h-1.5" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {summaryCards.map((item) => (
          <Card key={item.label} className="h-full border-border/70">
            <CardContent className="flex h-full min-h-[92px] flex-col justify-between p-2.5 sm:min-h-[120px] sm:p-4">
              <div className="space-y-1 sm:space-y-2">
                <p className="text-[8px] font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">{item.label}</p>
                <div className="font-data text-[1.35rem] font-semibold leading-none text-foreground sm:text-2xl">{item.value}</div>
              </div>
              <div>
                {"progress" in item && typeof item.progress === "number" ? (
                  <div className="space-y-1 sm:space-y-2">
                    <Progress value={item.progress} className="h-1.5 sm:h-2" />
                    <p className="text-[10px] leading-tight text-muted-foreground sm:text-xs">{item.note}</p>
                  </div>
                ) : (
                  <p className="text-[10px] leading-tight text-muted-foreground sm:text-xs">{item.note}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {fluxoLeveOficina && fotosObrigatorias && fotosPrincipaisPendentes.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/70">
          <CardContent className="flex flex-col gap-2 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">Fotos obrigatórias pendentes para finalizar</p>
                <p className="text-amber-800/80">
                  {fotosPrincipaisPendentes.map(getFriendlySectionLabel).join(", ")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {fluxoLeveOficina && (
        <Card className="border-border/70">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Dados do cliente</CardTitle>
            <CardDescription>
              Informe apenas o necessário para identificar o atendimento e facilitar o envio do termo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cliente-nome">Nome do cliente</Label>
                <Input
                  id="cliente-nome"
                  value={avaliacao.cliente_nome || ""}
                  onChange={(event) => updateClienteEntrada("cliente_nome", event.target.value)}
                  placeholder="Nome do proprietário ou responsável"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cliente-telefone">WhatsApp/telefone</Label>
                <Input
                  id="cliente-telefone"
                  value={avaliacao.cliente_telefone || ""}
                  onChange={(event) => updateClienteEntrada("cliente_telefone", event.target.value)}
                  placeholder="(00) 00000-0000"
                  inputMode="tel"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conteúdo guiado pela ordem da avaliação */}
      {(() => {
        const subcategorias = fluxoLeveOficina
          ? subcategoriasDaAvaliacao
          : gruposDaAvaliacao.find(g => g.nome === grupoAtual)?.subcategorias || [];
        const firstIncompleteIdx = subcategorias.findIndex((subcat) => getStatusDaSubcategoria(subcat) !== "concluida");
        return (
          <>
            {subcategorias.map((subcat, idx) => {
              const categoriaTemFotoPrincipal = !!fotosPrincipais[subcat];
              const statusSubcategoria = getStatusDaSubcategoria(subcat)
              const criteriosSubcat = criterios.filter(c => c.categoria === subcat);
              const sectionLabel = getFriendlySectionLabel(subcat)
              const pendenciasSubcategoria = getPendenciasDaSubcategoria(subcat)
              const proximaSubcategoria = subcategoriasDaAvaliacao[subcategoriasDaAvaliacao.findIndex((item) => item === subcat) + 1]

              if (!fluxoLeveOficina && statusSubcategoria !== "concluida" && idx !== firstIncompleteIdx) return null;
              return (
                <div key={subcat} className="mb-8">
                  <h3 className="mb-3 mt-6 text-xl font-semibold">{sectionLabel}</h3>
                  <Card className="surface-panel">
                    <CardHeader>
                      <CardTitle className="text-[1.35rem]">Foto da {vertical.labels.labelPrincipal.toLowerCase()} · {sectionLabel}</CardTitle>
                      <CardDescription>
                        {fluxoLeveOficina
                          ? "Registre a foto desta parte para documentar o estado de entrada."
                          : getStageInstruction(subcat)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PhotoUploadPrincipal
                        photo={fotosPrincipais[subcat] || ''}
                        onPhotoUpdate={(photoUrl) => updateFotoPrincipal(subcat, photoUrl)}
                        coverPhoto={avaliacao.veiculos.foto_principal}
                        onReuseCoverPhoto={subcat === "Frontal" && avaliacao.veiculos.foto_principal ? reutilizarFotoCapaComoFotoFrontal : undefined}
                        categoryLabel={subcat}
                        id={`photo-upload-${subcat}`}
                        key={`photo-upload-${subcat}`}
                      />
                    </CardContent>
                  </Card>
                  {/* Lista de Critérios da Subcategoria */}
                  {(categoriaTemFotoPrincipal || fluxoLeveOficina) && (
                    <div className="mt-4 space-y-4">
                      {criteriosSubcat.map((criterio) => {
                        const avaliacaoCriterio = avaliacoesCriterios[criterio.id]
                        return (
                          <Card
                            key={criterio.id}
                            className={
                              getVisualStatus(criterio.id) === "aprovado"
                                ? "border-l-4 border-l-emerald-500"
                                : getVisualStatus(criterio.id) === "atencao"
                                  ? "border-l-4 border-l-amber-500"
                                  : getVisualStatus(criterio.id) === "reprovado"
                                    ? "border-l-4 border-l-rose-500"
                                    : getVisualStatus(criterio.id) === "na"
                                      ? "border-l-4 border-l-slate-400"
                                      : "border-l-4 border-l-border"
                            }
                          >
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle className="text-lg">{criterio.nome}</CardTitle>
                                  <CardDescription>{criterio.descricao}</CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getVisualStatus(criterio.id) === "aprovado" && (
                                    <Badge className="bg-green-100 text-green-800">
                                      <Check className="mr-1 h-3 w-3" />
                                      {mostrarCustos ? "Aprovado" : "Ok"}
                                    </Badge>
                                  )}
                                  {getVisualStatus(criterio.id) === "atencao" && (
                                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                                      <AlertTriangle className="mr-1 h-3 w-3" />
                                      Atenção
                                    </Badge>
                                  )}
                                  {getVisualStatus(criterio.id) === "reprovado" && (
                                    <Badge variant="destructive">
                                      <X className="mr-1 h-3 w-3" />
                                      {mostrarCustos ? "Reprovado" : "Dano"}
                                    </Badge>
                                  )}
                                  {getVisualStatus(criterio.id) === "na" && (
                                    <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-700">
                                      <CircleSlash className="mr-1 h-3 w-3" />
                                      Isento
                                    </Badge>
                                  )}
                                  {getVisualStatus(criterio.id) === "pendente" && (
                                    <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-600">
                                      <AlertTriangle className="mr-1 h-3 w-3" />
                                      Pendente
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                                {[
                                  { value: "aprovado" as const, label: mostrarCustos ? "Aprovado" : "Ok", icon: Check, activeClass: "border-emerald-600 bg-emerald-50 text-emerald-700" },
                                  { value: "atencao" as const, label: "Atenção", icon: AlertTriangle, activeClass: "border-amber-500 bg-amber-50 text-amber-700" },
                                  { value: "reprovado" as const, label: mostrarCustos ? "Reprovado" : "Dano", icon: X, activeClass: "border-rose-600 bg-rose-50 text-rose-700" },
                                  { value: "na" as const, label: "Isento", icon: CircleSlash, activeClass: "border-slate-500 bg-slate-100 text-slate-700" },
                                ].map((option) => {
                                  const Icon = option.icon
                                  const active = getVisualStatus(criterio.id) === option.value

                                  return (
                                    <Button
                                      key={option.value}
                                      type="button"
                                      variant="outline"
                                      className={`min-h-12 justify-center rounded-xl border-2 px-3 py-3 text-sm font-medium ${
                                        active ? option.activeClass : "border-border/70 bg-background"
                                      }`}
                                      onClick={() => handleVisualStatusChange(criterio.id, option.value)}
                                    >
                                      <Icon className="mr-2 h-4 w-4" />
                                      {option.label}
                                    </Button>
                                  )
                                })}
                              </div>

                              {(getVisualStatus(criterio.id) === "atencao" || getVisualStatus(criterio.id) === "reprovado") && (
                                <div className="space-y-4 rounded-2xl border border-border/70 bg-secondary/25 p-4">
                                  <div>
                                    <label className="mb-2 block text-sm font-medium">Observação</label>
                                    <Textarea
                                      placeholder={mostrarCustos ? "Descreva rapidamente o problema encontrado..." : "Descreva o dano, condição ou observação relevante..."}
                                      value={avaliacaoCriterio?.observacoes || ""}
                                      onChange={(e) => updateCriterio(criterio.id, 'observacoes', e.target.value)}
                                      className="min-h-[96px]"
                                    />
                                  </div>

                                  {mostrarCustos && getVisualStatus(criterio.id) === "reprovado" && (
                                    <div>
                                      <label className="mb-2 block text-sm font-medium">Custo estimado</label>
                                      <input
                                        type="text"
                                        placeholder="R$ 0,00"
                                        inputMode="decimal"
                                        value={custoDigitado[criterio.id] ?? ''}
                                        onChange={(e) => handleCustoChange(criterio.id, e.target.value)}
                                        onBlur={() => handleCustoBlur(criterio.id)}
                                        className="flex min-h-12 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                      />
                                    </div>
                                  )}

                                  <div>
                                    <label className="mb-2 block text-sm font-medium">Evidência do item</label>
                                    <PhotoUpload
                                      photos={avaliacaoCriterio?.fotos || []}
                                      onPhotosUpdate={(newPhotos) => updateCriterio(criterio.id, 'fotos', newPhotos)}
                                      maxPhotos={1}
                                      uniqueId={criterio.id}
                                    />
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        )
                      })}

                      {!fluxoLeveOficina && subcat === etapaAtual && (
                        <Card className="border-border/70 bg-secondary/25">
                          <CardContent className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-foreground">
                                {pendenciasSubcategoria > 0 ? "Faltam itens nesta etapa" : "Etapa pronta para avançar"}
                              </p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {pendenciasSubcategoria > 0
                                  ? `${pendenciasSubcategoria} critério${pendenciasSubcategoria > 1 ? "s" : ""} ainda precisa${pendenciasSubcategoria > 1 ? "m" : ""} de decisão nesta etapa.`
                                    : proximaSubcategoria
                                      ? `Continue para ${getFriendlySectionLabel(proximaSubcategoria)}.`
                                    : `Esta é a última etapa da ${vertical.labels.labelPrincipal.toLowerCase()}.`}
                              </p>
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row">
                              {pendenciasSubcategoria === 0 ? (
                                <Button
                                  size="lg"
                                  className="w-full shadow-lg shadow-primary/20 sm:w-auto"
                                  onClick={() => void handleSalvarEAvancar(subcat)}
                                  disabled={avaliacaoFinalizada && !fluxoLeveOficina}
                                >
                                  <Save className="mr-2 h-4 w-4" />
                                  {proximaSubcategoria
                                    ? `Continuar para ${getFriendlySectionLabel(proximaSubcategoria)}`
                                    : "Concluir etapa"}
                                </Button>
                              ) : (
                                <Button variant="outline" disabled>
                                  <AlertTriangle className="mr-2 h-4 w-4" />
                                  Revise os pendentes da etapa
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        );
      })()}

      {deveMostrarObservacoesGerais && (
        <Card className="border-border/70">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Observações gerais</CardTitle>
            <CardDescription>
              Use este campo apenas para contexto extra que valha para a {vertical.labels.labelPrincipal.toLowerCase()} inteira.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder={mostrarCustos ? "Ex.: veículo chegou com avaria no para-choque dianteiro" : "Ex.: veículo entrou com avaria no para-choque dianteiro"}
              value={avaliacao.observacoes_iniciais || ""}
              onChange={(e) => updateObservacoesIniciais(e.target.value)}
              className="min-h-[96px]"
            />
          </CardContent>
        </Card>
      )}

      <Card className="border-border/70">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {avaliacaoFinalizada && fluxoLeveOficina && !hasChanges ? (
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground sm:mr-auto">
              <Check className="h-4 w-4 text-green-500" />
              Entrada salva
            </span>
          ) : (
            <Button
              variant={ctaPrincipalAvanca ? "default" : "outline"}
              className={cn(
                "w-full sm:w-auto",
                ctaPrincipalAvanca ? "shadow-lg shadow-primary/20" : undefined,
                (avaliacaoFinalizada && !fluxoLeveOficina) ? "border-border/80 bg-muted text-muted-foreground hover:bg-muted hover:text-muted-foreground" : undefined
              )}
              onClick={() => {
                if (avaliacaoFinalizada && !fluxoLeveOficina) {
                  return
                }
                if (avaliacaoFinalizada && fluxoLeveOficina) {
                  void handleSalvarProgresso()
                  return
                }

                if (avaliacaoCompleta) {
                  if (!evidenciasMinimasCompletas) {
                    const pendentes = fotosPrincipaisPendentes.map(getFriendlySectionLabel).join(", ")
                    toast.info(`Adicione as fotos obrigatórias antes de finalizar: ${pendentes}.`)
                    return
                  }

                  void handleFinalizarAvaliacao()
                  return
                }

                if (!usuarioEstaNoGrupoDaEtapaAtual) {
                  handleIrParaEtapaAtual()
                  return
                }

                if (etapaAtualCompleta) {
                  void handleSalvarEAvancar(etapaAtual)
                  return
                }

                void handleSalvarProgresso()
              }}
              disabled={desabilitarCtaPrincipal}
            >
              <Save className="mr-2 h-4 w-4" />
              {saving && !ctaPrincipalAvanca ? "Salvando..." : ctaPrincipalLabel}
            </Button>
          )}

          {avaliacaoFinalizada && fluxoLeveOficina && (
            <Button
              variant={hasChanges ? "outline" : "default"}
              className={cn("w-full sm:w-auto", !hasChanges ? "shadow-lg shadow-primary/20" : undefined)}
              asChild
            >
              <Link to={`/proposta/${id}`}>
                <FileText className="mr-2 h-4 w-4" />
                Ver termo de entrada
              </Link>
            </Button>
          )}

          {avaliacaoFinalizada && fluxoLeveOficina && (
            <Button variant="ghost" className="w-full text-muted-foreground hover:text-foreground sm:w-auto" asChild>
              <Link to="/avaliacoes">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar para entradas
              </Link>
            </Button>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                className="w-full text-destructive hover:bg-transparent hover:text-destructive/90 sm:w-auto"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir {vertical.labels.labelPrincipal.toLowerCase()}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir a {vertical.labels.labelPrincipal.toLowerCase()} do veículo{" "}
                  <span className="font-medium text-foreground">
                    {avaliacao?.veiculos?.placa || "selecionado"}
                  </span>
                  ? Esta ação não pode ser desfeita e todos os dados relacionados
                  (critérios e fotos) também serão removidos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteAvaliacao}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Confirmar Exclusão
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </CardContent>
      </Card>

      <Dialog
        open={photosDialogOpen}
        onOpenChange={(open) => {
          setPhotosDialogOpen(open)
          if (!open) setPhotoZoom(null)
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl flex-col">
          <DialogHeader>
            <div className="space-y-4">
              <DialogTitle className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  <span>{mostrarCustos ? "Fotos e reparos" : "Fotos da entrada"}</span>
                </span>
                <Badge variant="outline" className="font-data text-sm tracking-[0.16em]">
                  {avaliacao.veiculos.placa}
                </Badge>
              </DialogTitle>

              <Tabs value={photoTab} onValueChange={(value) => setPhotoTab(value as "reparos" | "atencao" | "gerais")} className="w-full">
                <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-[calc(var(--radius)+0.05rem)] border border-border/60 bg-white/65 p-1 shadow-[0_1px_2px_hsl(215_25%_20%_/_0.04)]">
                  <TabsTrigger value="reparos" className="flex items-center justify-between gap-2 rounded-[calc(var(--radius)-0.1rem)] px-3 py-2.5">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <span>{textosAvaliacao.labelFotosCriticas}</span>
                    </span>
                    <span className="font-data text-xs">{reparosIdentificados.length}</span>
                  </TabsTrigger>
                  <TabsTrigger value="atencao" className="flex items-center justify-between gap-2 rounded-[calc(var(--radius)-0.1rem)] px-3 py-2.5">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span>Atenção</span>
                    </span>
                    <span className="font-data text-xs">{atencaoIdentificados.length}</span>
                  </TabsTrigger>
                  <TabsTrigger value="gerais" className="flex items-center justify-between gap-2 rounded-[calc(var(--radius)-0.1rem)] px-3 py-2.5">
                    <span className="flex items-center gap-2">
                      <Camera className="h-4 w-4 text-muted-foreground" />
                      <span>Fotos gerais</span>
                    </span>
                    <span className="font-data text-xs">{fotosGerais.length}</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="reparos" className="mt-4">
                  <div className="max-h-[58vh] space-y-3 overflow-y-auto pr-1">
                    {reparosIdentificados.length > 0 ? (
                      reparosIdentificados.map((reparo) => (
                        <div key={reparo.criterioId} className="flex gap-3 rounded-xl border bg-white p-3 shadow-sm">
                          {reparo.foto ? (
                            <img
                              src={reparo.foto.url}
                              alt={reparo.nome}
                              onClick={() => setPhotoZoom({ url: reparo.foto!.url, title: reparo.nome, subtitle: reparo.categoria || (mostrarCustos ? "Reparo identificado" : "Dano registrado") })}
                              className="h-20 w-20 cursor-pointer rounded-lg border object-cover transition-opacity hover:opacity-90"
                            />
                          ) : (
                            <div className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border bg-muted px-2 text-center">
                              <Camera className="h-6 w-6 text-muted-foreground opacity-40" />
                              <span className="text-[10px] font-medium leading-tight text-muted-foreground">
                                Sem foto
                              </span>
                            </div>
                          )}
                          <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium leading-tight">{reparo.nome}</p>
                                {reparo.categoria && <p className="mt-0.5 text-xs text-muted-foreground">{reparo.categoria}</p>}
                              </div>
                              {mostrarCustos ? (
                                <Badge variant="destructive" className="shrink-0 text-xs">
                                  {`R$ ${reparo.custo.toFixed(2)}`}
                                </Badge>
                              ) : (
                                <Badge variant="destructive" className="shrink-0 text-xs">
                                  Dano
                                </Badge>
                              )}
                            </div>
                            {reparo.observacoes && (
                              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                <span className="font-medium">Obs:</span> {reparo.observacoes}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-dashed px-6 py-12 text-center text-muted-foreground">
                        <AlertTriangle className="mx-auto mb-3 h-10 w-10 opacity-40" />
                        <p>{mostrarCustos ? "Nenhum reparo identificado nesta avaliação" : "Nenhum dano registrado nesta entrada"}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="atencao" className="mt-4">
                  <div className="max-h-[58vh] space-y-3 overflow-y-auto pr-1">
                    {atencaoIdentificados.length > 0 ? (
                      atencaoIdentificados.map((item) => (
                        <div key={item.criterioId} className="flex gap-3 rounded-xl border bg-white p-3 shadow-sm">
                          {item.foto ? (
                            <img
                              src={item.foto.url}
                              alt={item.nome}
                              onClick={() => setPhotoZoom({ url: item.foto!.url, title: item.nome, subtitle: item.categoria || "Ponto de atenção" })}
                              className="h-20 w-20 cursor-pointer rounded-lg border object-cover transition-opacity hover:opacity-90"
                            />
                          ) : (
                            <div className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border bg-muted px-2 text-center">
                              <Camera className="h-6 w-6 text-muted-foreground opacity-40" />
                              <span className="text-[10px] font-medium leading-tight text-muted-foreground">
                                Sem foto
                              </span>
                            </div>
                          )}
                          <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium leading-tight">{item.nome}</p>
                                {item.categoria && <p className="mt-0.5 text-xs text-muted-foreground">{item.categoria}</p>}
                              </div>
                              <Badge variant="outline" className="shrink-0 border-amber-300 bg-amber-50 text-amber-700 text-xs">
                                Atenção
                              </Badge>
                            </div>
                            {item.observacoes && (
                              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                <span className="font-medium">Obs:</span> {item.observacoes}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-dashed px-6 py-12 text-center text-muted-foreground">
                        <AlertTriangle className="mx-auto mb-3 h-10 w-10 opacity-40" />
                        <p>{mostrarCustos ? "Nenhum ponto de atenção nesta avaliação" : "Nenhum ponto de atenção nesta entrada"}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="gerais" className="mt-4">
                  {fotosGerais.length > 0 ? (
                    <div className="max-h-[58vh] overflow-y-auto pr-1">
                      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                        {fotosGerais.map((foto, index) => (
                          <div
                            key={`${foto.title}-${index}`}
                            onClick={() => setPhotoZoom({ url: foto.url, title: foto.title, subtitle: foto.subtitle })}
                            className="relative aspect-square cursor-pointer overflow-hidden rounded-xl border transition-opacity hover:opacity-90"
                          >
                            <img src={foto.url} alt={foto.title} className="h-full w-full object-cover" />
                            <div className="absolute inset-x-0 bottom-0 truncate bg-black/60 p-1.5 text-xs text-white">
                              {foto.title}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed px-6 py-12 text-center text-muted-foreground">
                      <Camera className="mx-auto mb-3 h-10 w-10 opacity-40" />
                      <p>{mostrarCustos ? "Nenhuma foto geral registrada nesta avaliação" : "Nenhuma foto geral registrada nesta entrada"}</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoZoom} onOpenChange={(open) => { if (!open) setPhotoZoom(null) }}>
        <DialogContent className="max-w-6xl border-none bg-transparent p-4 shadow-none sm:p-6">
          {photoZoom && (
            <div className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#050505] shadow-2xl ring-1 ring-white/5">
              <div className="flex items-center justify-between gap-4 border-b border-white/10 bg-black px-5 py-4 text-white">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold sm:text-base">{photoZoom.title}</p>
                  {photoZoom.subtitle && (
                    <p className="truncate text-xs text-white/65 sm:text-sm">{photoZoom.subtitle}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-center bg-[#050505] p-3 sm:p-5">
                <img
                  src={photoZoom.url}
                  alt={photoZoom.title}
                  className="max-h-[82vh] w-auto max-w-full rounded-xl object-contain"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AssistenteVozContextual
        onTranscricao={handleVozAvaliacao}
        processando={iaProcessando}
      />
    </div>
  )
}

export default Avaliacao360
